import { useState } from 'react';
import { Plus, Truck } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useAppSelector } from '@/app/hooks';
import { useFormat } from '@/hooks/useAccess';
import {
  useCreateTransferMutation, useProductsQuery, useTransfersQuery, useUnitsQuery,
  type Transfer, type TransferStatus,
} from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatDate, formatMoney } from '@/lib/format';
import {
  Alert, Badge, Button, Card, EmptyState, ErrorState, Input, LoadingState, Modal,
  PageHeader, Pagination, Select, Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { TransferDetailModal } from './TransferDetailModal';

export const TRANSFER_TONE: Record<TransferStatus, 'neutral' | 'brand' | 'success' | 'warning' | 'danger'> = {
  DRAFT: 'neutral',
  REQUESTED: 'warning',
  APPROVED: 'brand',
  DISPATCHED: 'brand',
  RECEIVED: 'success',
  CANCELLED: 'neutral',
  REJECTED: 'danger',
};

interface LineDraft {
  variantId: string;
  unitId: string;
  requestedQty: string;
}

export function TransfersPage() {
  const format = useFormat();
  const warehouses = useAppSelector((state) => state.tenant.context?.warehouses ?? []);

  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const { data, isLoading, isError, error, refetch, isFetching } = useTransfersQuery({
    page,
    limit: 25,
    status: status || undefined,
  });

  const { data: products } = useProductsQuery({ limit: 200, trackInventory: true });
  const { data: units } = useUnitsQuery({ limit: 100, isActive: true });
  const [createTransfer, createState] = useCreateTransferMutation();

  const [source, setSource] = useState('');
  const [destination, setDestination] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([{ variantId: '', unitId: '', requestedQty: '' }]);
  const [formError, setFormError] = useState<string | null>(null);

  // Products carry their variants; stock moves against a variant, so that is
  // what a transfer line names.
  const variantOptions = (products?.items ?? []).flatMap((product) =>
    (product.variants ?? []).map((variant) => ({
      id: variant.id,
      label: `${product.name} — ${variant.sku}`,
      baseUnitId: product.baseUnitId,
    })),
  );

  const resetForm = (): void => {
    setSource('');
    setDestination('');
    setNotes('');
    setLines([{ variantId: '', unitId: '', requestedQty: '' }]);
    setFormError(null);
  };

  const filledLines = lines.filter((line) => line.variantId && line.unitId && line.requestedQty);
  const canSubmit =
    source !== '' && destination !== '' && source !== destination && filledLines.length > 0;

  const submit = async (): Promise<void> => {
    setFormError(null);
    try {
      const created = await createTransfer({
        sourceWarehouseId: source,
        destinationWarehouseId: destination,
        notes: notes || undefined,
        lines: filledLines.map((line) => ({
          variantId: line.variantId,
          unitId: line.unitId,
          requestedQty: Number(line.requestedQty),
        })),
      }).unwrap();

      setCreating(false);
      resetForm();
      setOpenId(created.id);
    } catch (failure) {
      setFormError(apiErrorMessage(failure, 'That transfer could not be created'));
    }
  };

  const setLine = (index: number, patch: Partial<LineDraft>): void => {
    setLines((current) =>
      current.map((line, position) => (position === index ? { ...line, ...patch } : line)),
    );
  };

  return (
    <>
      <PageHeader
        title="Stock transfers"
        description="Move stock between warehouses. Goods leave on dispatch and arrive on receipt."
        action={
          <Can permission={P.STOCK_TRANSFER}>
            <Button
              icon={Plus}
              onClick={() => setCreating(true)}
              disabled={warehouses.length < 2}
              title={warehouses.length < 2 ? 'A transfer needs two warehouses' : undefined}
            >
              New transfer
            </Button>
          </Can>
        }
      />

      {warehouses.length < 2 && (
        <div className="mb-4">
          <Alert tone="info">
            Transfers move stock between warehouses, so you need at least two before creating one.
          </Alert>
        </div>
      )}

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-subtle p-4">
          <div className="w-full sm:w-56">
            <Select
              label="Status"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(1);
              }}
            >
              <option value="">All statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="REQUESTED">Awaiting approval</option>
              <option value="APPROVED">Approved</option>
              <option value="DISPATCHED">In transit</option>
              <option value="RECEIVED">Received</option>
              <option value="REJECTED">Rejected</option>
              <option value="CANCELLED">Cancelled</option>
            </Select>
          </div>
        </div>

        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={Truck}
            title={status ? 'Nothing with that status' : 'No transfers yet'}
            description={
              status
                ? 'Clear the filter to see every transfer.'
                : 'Create a transfer to move stock between warehouses.'
            }
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Number</Th>
                  <Th>From</Th>
                  <Th>To</Th>
                  <Th numeric>Lines</Th>
                  <Th numeric>Value</Th>
                  <Th>Created</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody className={isFetching ? 'opacity-60' : undefined}>
                {data?.items.map((transfer: Transfer) => (
                  <tr key={transfer.id} className="hover:bg-sunken">
                    <Td>
                      <button
                        type="button"
                        className="font-medium hover:underline"
                        onClick={() => setOpenId(transfer.id)}
                      >
                        {transfer.number}
                      </button>
                    </Td>
                    <Td className="text-secondary">{transfer.sourceWarehouse.name}</Td>
                    <Td className="text-secondary">{transfer.destinationWarehouse.name}</Td>
                    <Td numeric className="text-secondary">{transfer._count?.items ?? 0}</Td>
                    <Td numeric className="text-secondary">
                      {formatMoney(transfer.totalCost, format)}
                    </Td>
                    <Td className="text-secondary">{formatDate(transfer.createdAt, format)}</Td>
                    <Td>
                      <Badge tone={TRANSFER_TONE[transfer.status]}>
                        {transfer.status === 'DISPATCHED' ? 'in transit' : transfer.status.toLowerCase()}
                      </Badge>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {data && (
              <Pagination
                page={data.meta.page}
                totalPages={data.meta.totalPages}
                total={data.meta.total}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      <Modal
        open={creating}
        onClose={() => {
          setCreating(false);
          resetForm();
        }}
        title="New transfer"
        description="Created as a draft. Nothing moves until it is dispatched."
        size="lg"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setCreating(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button onClick={() => void submit()} loading={createState.isLoading} disabled={!canSubmit}>
              Create draft
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {formError && <Alert tone="danger">{formError}</Alert>}

          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="From"
              required
              value={source}
              onChange={(event) => setSource(event.target.value)}
            >
              <option value="">Source warehouse</option>
              {warehouses.map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
              ))}
            </Select>
            <Select
              label="To"
              required
              value={destination}
              onChange={(event) => setDestination(event.target.value)}
              error={
                source && destination && source === destination
                  ? 'Pick a different warehouse'
                  : undefined
              }
            >
              <option value="">Destination warehouse</option>
              {warehouses.map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
              ))}
            </Select>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium">Lines</p>
            <div className="space-y-3">
              {lines.map((line, index) => (
                <div key={index} className="flex flex-wrap items-end gap-3">
                  <div className="min-w-48 flex-1">
                    <Select
                      label={index === 0 ? 'Product' : undefined}
                      value={line.variantId}
                      onChange={(event) => {
                        const variant = variantOptions.find(
                          (option) => option.id === event.target.value,
                        );
                        // Default the unit to the product's base unit — the
                        // common case, and always a valid conversion.
                        setLine(index, {
                          variantId: event.target.value,
                          unitId: line.unitId || variant?.baseUnitId || '',
                        });
                      }}
                    >
                      <option value="">Select a product</option>
                      {variantOptions.map((option) => (
                        <option key={option.id} value={option.id}>{option.label}</option>
                      ))}
                    </Select>
                  </div>
                  <div className="w-28">
                    <Input
                      label={index === 0 ? 'Quantity' : undefined}
                      type="number"
                      min="0"
                      step="0.0001"
                      value={line.requestedQty}
                      onChange={(event) => setLine(index, { requestedQty: event.target.value })}
                    />
                  </div>
                  <div className="w-36">
                    <Select
                      label={index === 0 ? 'Unit' : undefined}
                      value={line.unitId}
                      onChange={(event) => setLine(index, { unitId: event.target.value })}
                    >
                      <option value="">Unit</option>
                      {units?.items.map((unit) => (
                        <option key={unit.id} value={unit.id}>{unit.symbol}</option>
                      ))}
                    </Select>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={lines.length === 1}
                    onClick={() =>
                      setLines((current) => current.filter((_, position) => position !== index))
                    }
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>

            <Button
              size="sm"
              variant="outline"
              className="mt-3"
              onClick={() =>
                setLines((current) => [...current, { variantId: '', unitId: '', requestedQty: '' }])
              }
            >
              Add line
            </Button>
          </div>

          <Input
            label="Notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </div>
      </Modal>

      {openId && <TransferDetailModal transferId={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}
